import React, {
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import {
  View,
  type NativeSyntheticEvent,
  type StyleProp,
  type TextInput,
  type TextInputKeyPressEventData,
  type TextStyle,
} from 'react-native';
import DigitInput from '../DigitInput';

type Password = 'first' | 'second' | 'third' | 'fourth';

export interface CodeDigitsFunctions {
  clear(): void;
}

interface CodeDigitsProps {
  secure?: boolean;
  error?: boolean;
  style?: StyleProp<TextStyle>;
  onChange(password: string): void;
  focus?: boolean;
  wrongCode?: boolean;
}

const CodeDigits = React.forwardRef<CodeDigitsFunctions, CodeDigitsProps>(
  ({ secure, error, onChange, focus = true, wrongCode }, ref) => {
    const [focused, setFocused] = useState<Password>();
    const [password, setPassword] = useState<{ [key: string]: string }>({});

    const firstInput = useRef<TextInput>(null);
    const secondInput = useRef<TextInput>(null);
    const thirdInput = useRef<TextInput>(null);
    const fourthInput = useRef<TextInput>(null);

    useImperativeHandle(ref, () => ({
      clear: () => {
        setPassword({});
        if (firstInput && firstInput.current) {
          firstInput.current.focus();
        }
      },
    }));

    const handleChangeText = useCallback(
      (key: Password, text: string) => {
        if (text) {
          switch (key) {
            case 'first':
              secondInput.current?.focus();
              break;
            case 'second':
              thirdInput.current?.focus();
              break;
            case 'third':
              fourthInput.current?.focus();
          }
        }

        const pass = {
          ...password,
          [key]: text,
        };

        setPassword(pass);
        onChange(Object.values(pass).join(''));
      },
      [onChange, password]
    );

    const handleKeyPress = useCallback(
      (e: NativeSyntheticEvent<TextInputKeyPressEventData>, key: Password) => {
        if (e.nativeEvent.key === 'Backspace' && !password[key]) {
          switch (key) {
            case 'fourth':
              thirdInput.current?.focus();
              break;
            case 'third':
              secondInput.current?.focus();
              break;
            case 'second':
              firstInput.current?.focus();
              break;
          }
        }
      },
      [password]
    );

    const handleFocus = useCallback(
      (input: Password, inputRef: TextInput | null) => {
        setFocused(input);

        if (inputRef && inputRef.props && inputRef.props.value) {
          inputRef.setNativeProps({
            selection: { start: 1, end: 1 },
          });
        }
      },
      []
    );

    return (
      <View className="flex flex-row items-center justify-center">
        <DigitInput
          autoFocus={focus}
          ref={firstInput}
          withoutMargin
          error={!!error}
          focused={focused === 'first' || !!password.first}
          onFocus={() => handleFocus('first', firstInput.current)}
          onChangeText={(text) => handleChangeText('first', text)}
          onKeyPress={(e) => handleKeyPress(e, 'first')}
          value={password.first}
          secureTextEntry={secure}
          wrongCode={wrongCode}
        />
        <DigitInput
          ref={secondInput}
          error={!!error}
          focused={focused === 'second' || !!password.second}
          onFocus={() => handleFocus('second', secondInput.current)}
          onChangeText={(text) => handleChangeText('second', text)}
          onKeyPress={(e) => handleKeyPress(e, 'second')}
          value={password.second}
          secureTextEntry={secure}
          wrongCode={wrongCode}
        />
        <DigitInput
          ref={thirdInput}
          error={!!error}
          focused={focused === 'third' || !!password.third}
          onFocus={() => handleFocus('third', thirdInput.current)}
          onChangeText={(text) => handleChangeText('third', text)}
          onKeyPress={(e) => handleKeyPress(e, 'third')}
          value={password.third}
          secureTextEntry={secure}
          wrongCode={wrongCode}
        />
        <DigitInput
          ref={fourthInput}
          error={!!error}
          focused={focused === 'fourth' || !!password.fourth}
          onFocus={() => handleFocus('fourth', fourthInput.current)}
          onChangeText={(text) => handleChangeText('fourth', text)}
          onKeyPress={(e) => handleKeyPress(e, 'fourth')}
          value={password.fourth}
          secureTextEntry={secure}
          wrongCode={wrongCode}
        />
      </View>
    );
  }
);

export default CodeDigits;
